import React, { useRef, useState } from "react";

import { useMediaQuery } from "../../hooks/useMediaQuery";
import TeamCrest from "./shared/TeamCrest";
import { useCompetitionsTr } from "./shared/useCompetitionsTr";
import {
  formatScore,
  formatLegs,
  formatPenaltyScore,
  deriveWinner,
} from "./shared/competitionsFormat";

// CompetitionBracket — embeddable KO bracket (UI-SPEC §12 K2, §13 K5, §14 K2b,
// §20 K9).
//
// Geometry per spec: rounds as equal grid columns (gap 24, align-items
// stretch), matchups vertically distributed via justify-content:space-around —
// the tree stagger comes ONLY from that (no vertical connectors are specified).
// Connector = a 24×1px #E2E8F0 stub right of each card; the LAST round column
// renders no stub (§25.2: the mock's trailing stub is treated as an artefact).
//
// Edge cases (K5, verbatim rules):
//  - "Penalty results are a suffix line, never a replacement score."
//  - "Bye and undecided slots render as quiet empty cards · never invented
//    team names." (empty crest box + "TBD")
//  - Third-place match: detached dashed card outside the round chain, present
//    only when the provider returns it.
//
// Mobile/tablet (declared BEFORE build; spec note verbatim: "AT ≤1024 THE
// BRACKET SCROLLS HORIZONTALLY WITH A ROUND INDICATOR"): at ≤1024px the
// bracket scrolls HORIZONTALLY (230px columns, next column peeking) with the
// K9 round indicator above (label + 16×3px bars, active #2183FE) — no second
// nav mechanic, nothing is shrunk or dropped. Desktop ≥1025 keeps the equal
// grid columns.
//
// Self-contained: props in, markup out. Team rows expose an action slot via
// `renderTeamAction(team, matchup)`; default is profile navigation through the
// injected `onNavigate(team, matchup)` callback (no router dependency).

const BORDER = "#E2E8F0";

const SCOPED_CSS = `
.tp-comp-bracket-card { transition: border-color .12s ease; }
.tp-comp-bracket-card:hover { border-color: #2183FE; }
.tp-comp-bracket-team--click { cursor: pointer; }
.tp-comp-bracket-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
`;

const TeamRow = ({ team, matchup, emphasis, showCrests, onNavigate, navigable, renderTeamAction, tr }) => {
  const color =
    emphasis === "winner" ? "#0F172A" : emphasis === "loser" ? "#64748B" : "#94A3B8";
  const weight = emphasis === "winner" ? 700 : 500;
  const clickable =
    Boolean(team) &&
    typeof onNavigate === "function" &&
    !renderTeamAction &&
    // Per-row affordance gate (TOTW pattern: no dead cursor on teams the
    // page cannot resolve to a club profile).
    (typeof navigable !== "function" || Boolean(navigable(team)));
  return (
    <span
      data-testid="bracket-team-row"
      className={clickable ? "tp-comp-bracket-team--click" : undefined}
      onClick={clickable ? () => onNavigate(team, matchup) : undefined}
      style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}
    >
      {showCrests && <TeamCrest team={team} size={20} />}
      <span
        style={{
          fontSize: 13.5,
          fontWeight: weight,
          color,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: 1,
          minWidth: 0,
        }}
      >
        {team ? team.name : tr("TBD")}
      </span>
      {team && typeof renderTeamAction === "function" && (
        <span data-testid="team-action-slot" style={{ flex: "none" }}>
          {renderTeamAction(team, matchup)}
        </span>
      )}
    </span>
  );
};

const MatchupCard = ({
  matchup,
  dashed,
  showCrests,
  onNavigate,
  navigable,
  renderTeamAction,
  tr,
  trAny,
}) => {
  const winner = deriveWinner(matchup);
  const emphasisOf = (side, team) => {
    if (!team) return "tbd";
    if (!winner) return "loser"; // undecided: both regular weight/#64748B
    return winner === side ? "winner" : "loser";
  };

  // Footer: left = meta ("Single leg · Wembley") or two-leg line "2–1 · 2–2";
  // right = aggregate "4 : 3", or the single-match score. Penalties NEVER
  // replace the score (K5) — they become the suffix note line below.
  const legsText = matchup.meta || formatLegs(matchup.legs);
  const scorePair =
    Array.isArray(matchup.aggregate) &&
    matchup.aggregate[0] != null &&
    matchup.aggregate[1] != null
      ? matchup.aggregate
      : matchup.homeScore != null && matchup.awayScore != null
        ? [matchup.homeScore, matchup.awayScore]
        : null;
  const scoreText = scorePair ? formatScore(scorePair[0], scorePair[1]) : null;

  const pensText = formatPenaltyScore(matchup.penalties);
  const pensWinner =
    pensText && winner ? (winner === "home" ? matchup.home : matchup.away) : null;
  const penaltyLine =
    pensText && pensWinner
      ? trAny(`${pensWinner.name} win ${pensText} on penalties`)
      : null;

  return (
    <div
      data-testid="bracket-card"
      className="tp-comp-bracket-card"
      style={{
        border: dashed ? "1px dashed #C7D3E1" : `1px solid ${BORDER}`,
        borderRadius: 3,
        padding: "9px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 7,
        background: "#fff",
        flex: 1,
        minWidth: 0,
      }}
    >
      <TeamRow
        team={matchup.home}
        matchup={matchup}
        emphasis={emphasisOf("home", matchup.home)}
        showCrests={showCrests}
        onNavigate={onNavigate}
        navigable={navigable}
        renderTeamAction={renderTeamAction}
        tr={tr}
      />
      <TeamRow
        team={matchup.away}
        matchup={matchup}
        emphasis={emphasisOf("away", matchup.away)}
        showCrests={showCrests}
        onNavigate={onNavigate}
        navigable={navigable}
        renderTeamAction={renderTeamAction}
        tr={tr}
      />
      {(legsText || scoreText) && (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            paddingTop: 6,
            borderTop: "1px solid #F4F7FA",
          }}
        >
          <span
            style={{
              fontSize: 12,
              color: "#94A3B8",
              fontVariantNumeric: "tabular-nums",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {legsText ? trAny(legsText) : null}
          </span>
          {scoreText && (
            <span
              data-testid="bracket-score"
              style={{
                fontSize: 13.5,
                fontWeight: 700,
                color: "#0F172A",
                fontVariantNumeric: "tabular-nums",
                flex: "none",
              }}
            >
              {scoreText}
            </span>
          )}
        </span>
      )}
      {penaltyLine && (
        <span
          data-testid="penalty-note"
          style={{ fontSize: 11.5, color: "#475569", lineHeight: 1.5 }}
        >
          {penaltyLine}
        </span>
      )}
      {!penaltyLine && matchup.note && (
        <span style={{ fontSize: 11.5, color: "#475569", lineHeight: 1.5 }}>
          {trAny(matchup.note)}
        </span>
      )}
    </div>
  );
};

const RoundColumn = ({
  round,
  isLast,
  mobile,
  title,
  dashed,
  showCrests,
  onNavigate,
  navigable,
  renderTeamAction,
  tr,
  trAny,
}) => (
  <div
    data-testid="bracket-round"
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 12,
      width: mobile ? 230 : undefined,
      flex: mobile ? "none" : undefined,
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: ".09em",
        color: "#334155",
        paddingBottom: 8,
        borderBottom: "1px solid #EDF1F6",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {title}
    </div>
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around",
        gap: 14,
        flex: 1,
      }}
    >
      {round.matchups.map((matchup, index) => (
        <div
          key={matchup.id || index}
          style={{ display: "flex", alignItems: "center" }}
        >
          <MatchupCard
            matchup={matchup}
            dashed={dashed}
            showCrests={showCrests && !mobile}
            onNavigate={onNavigate}
            navigable={navigable}
            renderTeamAction={renderTeamAction}
            tr={tr}
            trAny={trAny}
          />
          {!isLast && !mobile && (
            <span
              data-testid="connector-stub"
              style={{ width: 24, height: 1, background: BORDER, flex: "none" }}
            />
          )}
        </div>
      ))}
    </div>
  </div>
);

const CompetitionBracket = ({
  rounds,
  thirdPlace,
  showCrests = true,
  onNavigate,
  navigable,
  renderTeamAction,
}) => {
  const { tr, trAny } = useCompetitionsTr();
  const isMobile = useMediaQuery("(max-width: 1024px)");
  const scrollRef = useRef(null);
  const [activeRound, setActiveRound] = useState(0);

  const visibleRounds = (Array.isArray(rounds) ? rounds : []).filter(
    (r) => r && Array.isArray(r.matchups) && r.matchups.length > 0,
  );
  if (visibleRounds.length === 0) return null; // empty renders nothing

  const columns = visibleRounds.map((round, index) => ({
    round,
    title: trAny(round.name || ""),
    dashed: false,
    isLast: index === visibleRounds.length - 1 && !thirdPlace,
  }));
  if (thirdPlace) {
    columns.push({
      round: { id: "third-place", matchups: [thirdPlace] },
      title: tr("Third-place match"),
      dashed: true, // detached card, outside the round chain (K5)
      isLast: true,
    });
  }

  const shared = { showCrests, onNavigate, navigable, renderTeamAction, tr, trAny };

  if (isMobile) {
    const COLUMN_WIDTH = 230;
    const COLUMN_GAP = 16;
    const onScroll = () => {
      const el = scrollRef.current;
      if (!el) return;
      const index = Math.round(el.scrollLeft / (COLUMN_WIDTH + COLUMN_GAP));
      setActiveRound(Math.max(0, Math.min(columns.length - 1, index)));
    };
    const active = columns[activeRound] || columns[0];
    return (
      <div className="tp-comp-bracket" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <style>{SCOPED_CSS}</style>
        <div
          data-testid="round-indicator"
          style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}
        >
          <span style={{ fontSize: 11, color: "#64748B", fontVariantNumeric: "tabular-nums" }}>
            {(active.round && active.round.abbr) || active.title}
          </span>
          <span style={{ display: "inline-flex", gap: 4 }}>
            {columns.map((column, index) => (
              <span
                key={column.round.id || index}
                data-testid="round-indicator-bar"
                style={{
                  width: 16,
                  height: 3,
                  borderRadius: 2,
                  background: index === activeRound ? "#2183FE" : BORDER,
                }}
              />
            ))}
          </span>
        </div>
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="tp-comp-bracket-scroll"
          style={{ display: "flex", gap: COLUMN_GAP, alignItems: "stretch" }}
        >
          {columns.map((column, index) => (
            <RoundColumn
              key={column.round.id || index}
              round={column.round}
              title={column.title}
              dashed={column.dashed}
              isLast={column.isLast}
              mobile
              {...shared}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="tp-comp-bracket">
      <style>{SCOPED_CSS}</style>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${columns.length}, minmax(0,1fr))`,
          gap: 24,
          alignItems: "stretch",
        }}
      >
        {columns.map((column, index) => (
          <RoundColumn
            key={column.round.id || index}
            round={column.round}
            title={column.title}
            dashed={column.dashed}
            isLast={column.isLast}
            mobile={false}
            {...shared}
          />
        ))}
      </div>
    </div>
  );
};

export default CompetitionBracket;
